import { Alert, Badge, Card, Group, Stack, Text, Tooltip } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { GenerationBadge } from "@/components/GenerationBadge";
import { MigrationStateCell } from "@/components/MigrationStall";
import { RelatedLink } from "@/components/RelatedLink";
import { StatusBadge } from "@/components/StatusBadge";
import { Timestamp } from "@/components/Timestamp";
import { EmptyState } from "@/components/states";
import { col } from "@/entities/columns";
import { COLLECTIONS } from "@/firestore/collections";
import { DOCUMENT_ID, useFirestoreInQuery, useFirestoreQuery } from "@/firestore/hooks";
import type { WalletDoc, WalletMigrationDoc } from "@/firestore/types";
import { useNow } from "@/lib/useNow";
import { entityPath } from "@/routes/paths";
import {
  ACTIVE_MIGRATION_STATES,
  STUCK_REASON_LABELS,
  countByReason,
  selectStuckMigrations,
} from "@/wallet/migrations";
import type { StuckReason, StuckRow } from "@/wallet/migrations";

const ACTIVE_LIMIT = 200;
const MIGRATING_LIMIT = 200;

const REASON_COLOR: Record<StuckReason, string> = {
  failedSourceMigrating: "red",
  migratingWithoutRow: "red",
  stalled: "orange",
  cleanupNotScheduled: "yellow",
  inFlight: "blue",
};

const REASON_HELP: Record<StuckReason, string> = {
  failedSourceMigrating:
    "The migration failed after its successor got addresses, so the source stays migrating and the owner is locked out of new actions. Abandon restores the source.",
  migratingWithoutRow:
    "The wallet is migrating but no live migration owns it (the sweep pages migratingWithoutRow). Investigate before acting.",
  stalled: "Past its state's stall threshold: the backend's sweep is paging it.",
  cleanupNotScheduled:
    "Completed, but its +24 h cleanup was never scheduled. Redrive re-schedules it.",
  inFlight: "Active and within its threshold.",
};

function columns(now: number) {
  return [
    col<StuckRow>({
      id: "reason",
      header: "Why listed",
      value: (r) => STUCK_REASON_LABELS[r.reason],
      cell: (r) => (
        <Tooltip label={REASON_HELP[r.reason]} multiline w={320} withArrow>
          <Badge color={REASON_COLOR[r.reason]} variant="light" radius="sm" tt="none">
            {STUCK_REASON_LABELS[r.reason]}
          </Badge>
        </Tooltip>
      ),
    }),
    col<StuckRow>({
      id: "migration",
      header: "Migration",
      value: (r) => r.migration?._docId ?? "",
      cell: (r) => <RelatedLink entity="walletMigration" id={r.migration?._docId} />,
    }),
    col<StuckRow>({
      id: "state",
      header: "State",
      value: (r) => r.migration?.state ?? "",
      cell: (r) =>
        r.migration ? (
          <MigrationStateCell migration={r.migration} now={now} />
        ) : (
          <Text c="dimmed">—</Text>
        ),
    }),
    col<StuckRow>({
      id: "stallReason",
      header: "Stall reason",
      value: (r) => r.stallReason?.label ?? "",
      cell: (r) =>
        r.stallReason ? (
          <Tooltip label={r.stallReason.code} withArrow>
            <Text size="sm">{r.stallReason.label}</Text>
          </Tooltip>
        ) : (
          <Text c="dimmed">—</Text>
        ),
    }),
    col<StuckRow>({
      id: "waitingOn",
      header: "Waiting on",
      value: (r) => r.waitingOn ?? "",
      cell: (r) => <Text size="sm">{r.waitingOn ?? "—"}</Text>,
    }),
    col<StuckRow>({
      id: "automatic",
      header: "Sweep",
      value: (r) => (r.automaticActions ? "acting" : "paging only"),
      cell: (r) =>
        r.migration &&
        r.reason !== "failedSourceMigrating" &&
        r.reason !== "migratingWithoutRow" ? (
          <Tooltip
            label={
              r.automaticActions
                ? "Within its 24 h window (automaticActionsUntil): the stale sweep still reconciles and re-drives it."
                : "Past its 24 h window: the sweep only pages it. An operator must redrive or abandon."
            }
            multiline
            w={300}
            withArrow
          >
            <Badge
              color={r.automaticActions ? "green" : "orange"}
              variant="light"
              radius="sm"
              tt="none"
            >
              {r.automaticActions ? "Acting" : "Operator only"}
            </Badge>
          </Tooltip>
        ) : (
          <Text c="dimmed">—</Text>
        ),
    }),
    col<StuckRow>({
      id: "generations",
      header: "Generations",
      value: (r) =>
        r.migration ? `${r.migration.fromGeneration} → ${r.migration.toGeneration}` : "",
      cell: (r) =>
        r.migration ? (
          <Group gap={4} wrap="nowrap">
            <GenerationBadge generation={r.migration.fromGeneration} />
            <Text size="sm">→</Text>
            <GenerationBadge generation={r.migration.toGeneration} />
          </Group>
        ) : (
          <Text c="dimmed">—</Text>
        ),
    }),
    col<StuckRow>({
      id: "source",
      header: "Source wallet",
      value: (r) => r.sourceWalletId,
      cell: (r) => (
        <Group gap={4} wrap="nowrap">
          <RelatedLink entity="wallet" id={r.sourceWalletId} />
          {r.sourceState ? <StatusBadge state={r.sourceState} /> : null}
        </Group>
      ),
    }),
    col<StuckRow>({
      id: "successor",
      header: "Successor",
      value: (r) => r.migration?.newWallet ?? "",
      cell: (r) => <RelatedLink entity="wallet" id={r.migration?.newWallet} />,
    }),
    col<StuckRow>({
      id: "owner",
      header: "User",
      value: (r) => r.owner ?? "",
      cell: (r) => <RelatedLink entity="user" id={r.owner} />,
    }),
    col<StuckRow>({
      id: "updatedAt",
      header: "Updated",
      value: (r) => r.sortAt,
      cell: (r) => <Timestamp value={r.migration?.updatedAt ?? new Date(r.sortAt).toISOString()} />,
    }),
  ];
}

/**
 * Migrations an operator may have to unwedge: every non-terminal migration, completed ones whose
 * cleanup was never scheduled, failed ones whose source wallet is still `migrating`, and migrating
 * wallets no live migration owns. Live listeners on `walletMigrations (state, updatedAt)` and
 * `wallets.state`; the per-source migrations are read in `oldWallet in` batches of 30.
 */
export function StuckMigrationsPage() {
  const navigate = useNavigate();
  const now = useNow();
  const active = useFirestoreQuery<WalletMigrationDoc>(COLLECTIONS.walletMigrations, {
    filters: [["state", "in", [...ACTIVE_MIGRATION_STATES]]],
    orderBy: [["updatedAt", "asc"]],
    limit: ACTIVE_LIMIT,
  });
  const cleanupUnscheduled = useFirestoreQuery<WalletMigrationDoc>(COLLECTIONS.walletMigrations, {
    filters: [
      ["state", "==", "completed"],
      ["scheduledCleanupAt", "==", null],
    ],
    limit: 100,
  });
  const migratingWallets = useFirestoreQuery<WalletDoc>(COLLECTIONS.wallets, {
    filters: [["state", "==", "migrating"]],
    limit: MIGRATING_LIMIT,
  });
  const ofMigrating = useFirestoreInQuery<WalletMigrationDoc>(
    COLLECTIONS.walletMigrations,
    "oldWallet",
    migratingWallets.data.map((w) => w._docId),
  );
  const sources = useFirestoreInQuery<WalletDoc>(
    COLLECTIONS.wallets,
    DOCUMENT_ID,
    [...active.data, ...cleanupUnscheduled.data].map((m) => m.oldWallet),
  );

  const sourceMap = new Map(sources.data.map((w) => [w._docId, w]));
  const rows = selectStuckMigrations({
    active: active.data,
    cleanupUnscheduled: cleanupUnscheduled.data,
    migratingWallets: migratingWallets.data,
    migrationsOfMigratingWallets: ofMigrating.data,
    sourceWallets: sourceMap,
    now,
  });
  const counts = countByReason(rows);
  const loading =
    active.loading || cleanupUnscheduled.loading || migratingWallets.loading || ofMigrating.loading;
  const failures = [
    ["Active migrations", active.error],
    ["Completed migrations without cleanup", cleanupUnscheduled.error],
    ["Migrating wallets", migratingWallets.error],
    ["Migrations of migrating wallets", ofMigrating.error],
    ["Source wallets", sources.error],
  ].filter((entry): entry is [string, Error] => entry[1] instanceof Error);

  return (
    <>
      <PageHeader
        title="Stuck migrations"
        subtitle="Wallet migrations an operator may have to redrive, abandon or reactivate. Open a row for its steps and commands."
      />
      <Card withBorder radius="md" padding="md">
        <Stack gap="sm">
          {failures.length > 0 ? (
            <Alert color="red" variant="light" title="This list is incomplete">
              {failures.map(([label, error]) => (
                <Text key={label} size="sm">
                  {label}: {error.message}
                </Text>
              ))}
            </Alert>
          ) : null}
          {active.data.length >= ACTIVE_LIMIT || migratingWallets.data.length >= MIGRATING_LIMIT ? (
            <Alert color="orange" variant="light">
              Showing the oldest {ACTIVE_LIMIT} active migrations and {MIGRATING_LIMIT} migrating
              wallets: more exist.
            </Alert>
          ) : null}
          <Group gap="xs">
            {(Object.keys(STUCK_REASON_LABELS) as StuckReason[]).map((reason) => (
              <Tooltip key={reason} label={REASON_HELP[reason]} multiline w={320} withArrow>
                <Badge
                  color={REASON_COLOR[reason]}
                  variant={counts[reason] > 0 ? "filled" : "light"}
                  radius="sm"
                  tt="none"
                >
                  {STUCK_REASON_LABELS[reason]}: {counts[reason]}
                </Badge>
              </Tooltip>
            ))}
          </Group>
          <DataTable
            data={rows}
            columns={columns(now)}
            loading={loading}
            searchPlaceholder="Filter migrations…"
            onRowClick={(row) =>
              navigate(
                row.migration
                  ? entityPath("walletMigration", row.migration._docId)
                  : entityPath("wallet", row.sourceWalletId),
              )
            }
            emptyState={
              <EmptyState
                title="Nothing stuck"
                description="No migration is in flight, and no wallet is left migrating. New rows appear here live."
              />
            }
          />
        </Stack>
      </Card>
    </>
  );
}
